<template>
  <div class="flex flex-col gap-2">
    <label for="file-upload" class="block text-sm font-medium text-primary"
      >Attach files</label
    >
    <div class="flex flex-col-reverse md:flex-row gap-2 justify-between">
      <ul class="flex flex-col md:flex-row gap-1">
        <li
          v-for="(file, index) in files"
          :key="index"
          class="px-2 text-dark4 border-cadet-gray border-[1px] rounded border-opacity-10 text-xs flex justify-between md:justify-center items-center"
        >
          {{ getFileDisplayInfo(file) }}
          <BaseButton
            type="button"
            class="text-primary px-1"
            @click="removeFile(index)"
          >
            X
          </BaseButton>
        </li>
      </ul>
      <BaseButton
        class="flex items-center justify-center bg-secondary bg-opacity-10 text-primary px-6 py-2 rounded gap-1 min-w-[182px]"
        @click.prevent="triggerFileInput"
      >
        <svgo-uploadV2 />
        Upload new file
      </BaseButton>
      <input
        id="file-upload"
        ref="fileInput"
        type="file"
        class="hidden"
        multiple
        :accept="allowedFileTypes.join(', ')"
        @change="onChange"
      />
    </div>
    <div class="border-b-[1px] border-dark4 pt-2 md:pt-0" />
    <div v-if="error" class="text-red-500 font-normal">
      {{ error }}
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    allowedFileTypes,
    getFileSize,
    isValidFileName,
    isValidFileType
  } from '@/utils';
  import { ref } from 'vue';

  const fileInput = ref<HTMLInputElement | null>(null);
  const files = ref<File[]>([]);
  const error = ref('');
  const model = defineModel<File[]>();

  const triggerFileInput = () => {
    fileInput.value?.click();
  };

  const onChange = (event: Event) => {
    const selectedFiles = Array.from(
      (event.target as HTMLInputElement | null)?.files ?? []
    );

    error.value = '';
    const totalFiles = files.value.length + selectedFiles.length;

    const isInvalidFileName = selectedFiles.some(
      (file) => !isValidFileName(file.name)
    );

    if (isInvalidFileName) {
      error.value =
        'No special characters allowed. One or more files have an invalid file name.';
      return;
    }

    const isDuplicate = selectedFiles.some((newFile) =>
      files.value.some(
        (existingFile) =>
          existingFile.name === newFile?.name &&
          existingFile.size === newFile?.size
      )
    );

    const invalidFiles = selectedFiles.filter((file) => !isValidFileType(file));
    if (invalidFiles.length > 0) {
      error.value = 'One or more files have an invalid file type.';
      return;
    }

    if (isDuplicate) {
      error.value = 'Duplicate file detected. Please select a different file.';
      return;
    }

    if (totalFiles > 5) {
      error.value = 'You can only upload up to 5 files.';
      return;
    }

    const totalSize = selectedFiles.reduce(
      (acc, file) => acc + file.size,
      files.value.reduce((acc: number, file: File) => acc + file.size, 0)
    );

    if (totalSize > 500 * 1024 * 1024) {
      error.value = 'The total file size must be less than 100MB.';
      return;
    }

    files.value = [...files.value, ...selectedFiles];
    error.value = '';
    model.value = files.value;
  };

  const removeFile = (index: number) => {
    files.value = files.value.filter((_, i) => index !== i);
  };

  const truncate = (text: string, length: number) =>
    text.length > length ? `${text.substring(0, length)}...` : text;

  const getFileDisplayInfo = (file: File | null) => {
    const fileName = file?.name ? truncate(file.name, 15) : 'unknown file name';
    const fileSize = file?.size ? getFileSize(file.size) : '(0B)';
    return `${fileName} (${fileSize})`;
  };
</script>
